import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Banknote,
  Calendar,
  Heart,
  Shirt,
  UtensilsCrossed,
  PartyPopper,
  Trophy,
  ShoppingBag,
  GraduationCap,
  MapPin,
  Shield,
  PiggyBank
} from "lucide-react";

export default function BenefitsPage() {
  const benefitCategories = [
    {
      title: "Salary & Pay",
      icon: Banknote,
      color: "var(--rd-gold)",
      benefits: [
        { title: "Competitive Salary", description: "Consistent with industry standards and relevant to your role" },
        { title: "Annual Pay Rise", description: "In line with industry recommendations each year" },
        { title: "Salary Scales", description: "Clear salary structure for each position with progression opportunities" },
        { title: "Annualised Hours Contract", description: "Available for permanent staff in qualifying positions" },
      ]
    },
    {
      title: "Time Off",
      icon: Calendar,
      color: "var(--rd-green)",
      benefits: [
        { title: "31 Days Paid Leave", description: "Generous annual leave allowance (pro rata for casual hours staff)" },
        { title: "Paid Breaks", description: "Your meal breaks are paid — not deducted from your hours" },
      ]
    },
    {
      title: "Health & Protection",
      icon: Shield,
      color: "#ef4444",
      benefits: [
        { title: "Sick Pay", description: "4 weeks full pay, then 4 weeks half pay in any 12-month period (permanent staff)" },
        { title: "Group Income Protection", description: "Additional financial support if unable to work due to illness or injury from week 13 (T&Cs apply)" },
        { title: "Group Life Assurance", description: "Tax-free lump sum of 4x annual salary to your beneficiary (qualifying positions)" },
      ]
    },
    {
      title: "Pension",
      icon: PiggyBank,
      color: "#8b5cf6",
      benefits: [
        { title: "Contributory Pension Scheme", description: "Club contributes 5.25% when you contribute at least 4.25% — exceeding government targets" },
      ]
    },
    {
      title: "Meals & Uniform",
      icon: UtensilsCrossed,
      color: "#f59e0b",
      benefits: [
        { title: "Meals Supplied", description: "Complimentary meals provided during your shift" },
        { title: "Uniforms Supplied", description: "Full uniform provided — look the part without the cost" },
      ]
    },
    {
      title: "Golf Privileges",
      icon: Trophy,
      color: "#10b981",
      benefits: [
        { title: "Free Golf Club Membership", description: "Or course playing rights depending on your role — including members loyalty card" },
        { title: "Free Golf Lessons", description: "Complimentary lessons for beginners to help you learn the game" },
        { title: "Free Dornoch Squash Club Membership", description: "Keep fit with free access to the local squash club" },
      ]
    },
    {
      title: "Discounts & Tips",
      icon: ShoppingBag,
      color: "#3b82f6",
      benefits: [
        { title: "Pro Shop Discount", description: "Discounted merchandise from the pro shop" },
        { title: "Share of Tips", description: "Bar and catering department share tips received" },
        { title: "Share of Staff Fund", description: "All other departments share in the staff fund" },
      ]
    },
    {
      title: "Social & Development",
      icon: PartyPopper,
      color: "#ec4899",
      benefits: [
        { title: "Social Events", description: "At least 2 social events each year paid for by the club" },
        { title: "Training & Development", description: "We encourage all employees to grow their knowledge and skills through training and placement options" },
      ]
    },
  ];

  const highlights = [
    {
      icon: MapPin,
      title: "World's #3 Golf Course",
      description: "Golf Digest, January 2022"
    },
    {
      icon: Heart,
      title: "Best Location in the World",
      description: "To live and work (in our opinion!)"
    },
    {
      icon: GraduationCap,
      title: "Be Part of History",
      description: "Contribute to the club's success during an important era"
    }
  ];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8 animate-fade-in-up">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
            Benefits of Working for Royal Dornoch
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Your entitlements and benefits as a Royal Dornoch team member.
          </p>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {highlights.map((item) => (
            <Card key={item.title} className="border-[var(--rd-gold)] bg-gradient-to-br from-[var(--rd-gold)]/5 to-transparent">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--rd-gold)]/10 flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-[var(--rd-gold)]" />
                </div>
                <h3 className="font-semibold text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                  {item.title}
                </h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefitCategories.map((category, index) => (
            <Card 
              key={category.title} 
              className={`border-[hsl(var(--border))] stagger-${(index % 4) + 1} animate-fade-in-up`}
            >
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: `${category.color}15` }}
                  >
                    <category.icon className="w-5 h-5" style={{ color: category.color }} />
                  </div>
                  <span className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                    {category.title}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  {category.benefits.map((benefit) => (
                    <div key={benefit.title} className="border-l-2 border-[hsl(var(--border))] pl-4">
                      <h4 className="font-medium text-[hsl(var(--foreground))] text-sm">
                        {benefit.title}
                      </h4>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                        {benefit.description}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer Note */}
        <Card className="border-[hsl(var(--border))]">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Some benefits are subject to qualifying periods and positions. Full details are available in your employment contract.
              <br />
              <span className="text-[var(--rd-green)] font-medium mt-2 block">
                Questions? Speak to Scott or your Shift Leader.
              </span>
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
